import React from 'react';
import { FormState } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { Alert } from '@ast/magma/components/alert';

import { ResultWizardStep } from '@app/common/components/configurable-wizards/ResultWizardStep/ResultWizardStep';
import { processWizardClose } from '@app/common/components/configurable-wizards/wizardCompletion';
import { PageWithHeader } from '@app/common/components/PageWithHeader/PageWithHeader';
import { ConnectedWizardStep } from '@app/common/configurable-wizards';
import {
  SingleChoiceRadioButtonsFieldInfoLocator,
} from '@app/common/configurable-wizards/fields/SingleChoice/RadioButtons/SingleChoiceRadioButtonsField';
import { RetrievalWizardStepWithHeader } from '@app/common/configurable-wizards/RetrievalWizardStepWithHeader';

import { RetrievalState } from '@app/widgets/user-retrieval/constants';

import { useUserKindTranslation } from '../../hooks/useUserKindTranslation';

export const UserKindSelectStep: React.FC<ConnectedWizardStep> = ({
  locators,
  ...props
}) => {
  const { t } = useUserKindTranslation();
  const location = useLocation<RetrievalState>();
  const retrievalType = location.state?.retrievalType;

  if (retrievalType === undefined) {
    return (
      <PageWithHeader>
        <ResultWizardStep
          level="error"
          title={t('retrieval-wizard.error-result.title|Retrieval wizard error dialog title',
            'Retrieval process cannot be completed')}
          buttonText={
            t('retrieval-wizard.result.return-to-login-button|Retrieval wizard - return to login button text',
              'Return to login')
          }
          // eslint-disable-next-line i18next/no-literal-string
          buttonStableName="ReturnToLoginButton"
          handleButtonClick={() => {
            processWizardClose();
            return true;
          }}
        />
      </PageWithHeader>
    );
  }

  const stepLocators = [
    SingleChoiceRadioButtonsFieldInfoLocator,
    ...(locators || []),
  ];

  const { beforeFieldsContent, ...restProps } = props;
  const beforeFieldsContentWithAlert = (data: {
    formState: FormState<any>
  }) => (
    <>
      {beforeFieldsContent && beforeFieldsContent(data)}
      <Alert
        level="warning"
        title={t('usertype-selection.force-password-reset-alert.title|Force password reset alert title',
          'To protect the security of your account, you\'ve been asked to reset your password')}
      />
    </>
  );
  const showAlert = location.state?.showForcePasswordResetAlert;

  return (
    <RetrievalWizardStepWithHeader
      title={t(
        'usertype-selection.password-retrieval.title|Usertype Selection step with password retrieval title',
        'Forgot your password?',
      )}
      usernameTitle={t(
        'usertype-selection.username-retrieval.title|Usertype Selection step with username retrieval title',
        'Forgot your username?',
      )}
      description={t('usertype-selection.description|Usertype Selection step description',
        'No problem! Please specify which type of account you are trying to access.')}
      stepLocators={stepLocators}
      wizardStepProps={{
        beforeFieldsContent: showAlert ? beforeFieldsContentWithAlert : beforeFieldsContent,
        ...restProps,
      }}
    />
  );
};
